import { divIcon, LatLngBoundsExpression, MarkerCluster, Point } from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useState } from "react";
import { useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { IDetour, IPOI } from "./map";
import { AllMarkers } from "./marker-group";
import { detourMarkersIcons } from "./markers";

import { captureException } from "@sentry/react";
import bbox from "@turf/bbox";
import { AllGeoJSON, pointOnFeature } from "@turf/turf";
import { ILine } from "./detour-lines";

interface DetourMarkerData {
  item: IDetour;
  point: GeoPoint;
  type: "detour";
}

interface POIMarkerData {
  item: IPOI;
  point: GeoPoint;
  type: "poi";
}

// Union type for marker data
export type MarkerData = DetourMarkerData | POIMarkerData;

type GeoPoint = {
  geometry: {
    coordinates: [number, number];
  };
};

export default function CombinedMarkers({
  detours,
  lines,
  pois,
}: {
  detours: IDetour[] | [];
  lines: ILine[] | [];
  pois: IPOI[] | [];
}) {
  const map = useMap();
  const [onDetoursDetail, setOnDetoursDetail] = useState(false);
  const [onPoiDetail, setPoiDetail] = useState(false);
  const hideIcons =
    onDetoursDetail && !onPoiDetail && detours[0].status != "Verwachte werkzaamheden";
  const hidePopup =
    onPoiDetail || (onDetoursDetail && detours[0].status != "Verwachte werkzaamheden");

  const markerData: MarkerData[] = [
    ...detours.map(detourToMarkerData),
    ...pois.map(poiToMarkerData),
  ].filter((markerData) => !!markerData);

  // /**
  //  * When detours changes recalculate center
  //  */
  useEffect(() => {
    try {
      setOnDetoursDetail(
        detours.length == 1 && pois.length < 1 && detours[0].detourType.name != "Evenementen"
      );
      setPoiDetail(pois.length == 1 && detours.length < 1);
      let geo: AllGeoJSON | null = null;

      if (onDetoursDetail && lines.length > 0) {
        geo = {
          type: "GeometryCollection",
          geometries: lines.map((line) => ({
            type: "LineString",
            coordinates: line.refinedGeometry.coordinates,
          })),
        };
      } else if (markerData.length > 0) {
        geo = {
          type: "GeometryCollection",
          geometries: markerData.map(({ point }) => ({
            type: "Point",
            coordinates: point.geometry.coordinates,
          })),
        };
      }

      if (!geo) return;

      // returns [minX, minY, maxX, maxY]
      const bounds = bbox(geo);
      const isInvalidBounds = bounds.some((i) => isNaN(i) || !Number.isFinite(i));
      if (isInvalidBounds) {
        return;
      }

      const boundExpression: LatLngBoundsExpression = [
        [bounds[1], bounds[0]],
        [bounds[3], bounds[2]],
      ];
      map.fitBounds(boundExpression, { animate: false });
    } catch (error) {
      console.error(error);
      captureException(error);
    }
  }, [lines, detours, markerData]);

  if (hideIcons) {
    return <></>;
  }

  // Only markers with status "verwachte werkzaamheden" are shown until zoom level 15
  return (
    <MarkerClusterGroup
      iconCreateFunction={(cluster) => createClusterIcon(markerData, cluster)}
      removeOutsideVisibleBounds={false}
    >
      <AllMarkers markerData={markerData} hidePopup={hidePopup} />
    </MarkerClusterGroup>
  );
}

function detourToMarkerData(detour: IDetour): DetourMarkerData | null {
  const fases = [];

  if (detour.status === "Verwachte werkzaamheden") {
    detour.fases.forEach((fase: any) => {
      fases.push(fase);
    });
  }

  if (detour.fases.length) {
    // find the first fase that has detourlines and a status of "in uitvoering" or "op de planning"
    const fase = detour.fases.find(
      (fase: any) =>
        fase.detourlines.length &&
        ["In uitvoering", "Op de planning", "Verwachte werkzaamheden"].includes(fase.status)
    );
    if (
      fase &&
      fase.detourlines.some(
        (line: any) =>
          ["ClosingLine", "RestrictedLine"].includes(line.type) &&
          line.refinedGeometry.coordinates.length
      )
    ) {
      fases.push(fase);
    }
  }

  if (!fases.length) {
    return null;
  }

  const point = pointOnFeature({
    type: "FeatureCollection",
    features: fases.map((fase) => {
      // Check for "ClosingLine" first, then "RestrictedLine"
      const preferedPoints = fase.detourlines
        .filter((line) => ["ClosingLine", "Point"].includes(line.type))
        .flatMap((line) => {
          return line.refinedGeometry;
        });

      const alternativePoints = fase.detourlines
        .filter((line) => ["ClosingLine", "RestrictedLine", "Point"].includes(line.type))
        .flatMap((line) => {
          return line.refinedGeometry;
        });

      return {
        type: "Feature",
        properties: {},
        geometry: {
          type: "GeometryCollection",
          geometries: preferedPoints.length > 0 ? preferedPoints : (alternativePoints as any),
        },
      };
    }),
  });

  return {
    item: detour,
    point: point as unknown as GeoPoint,
    type: "detour",
  };
}

function poiToMarkerData(poi: IPOI): POIMarkerData {
  if (poi.geometry.coordinates) {
    const point: GeoPoint = {
      geometry: {
        coordinates: [poi.geometry.coordinates[0], poi.geometry.coordinates[1]],
      },
    };
    return { item: poi, point, type: "poi" };
  } else {
    const point: GeoPoint = {
      geometry: {
        coordinates: [
          poi.geometry.geometries[0].coordinates[0],
          poi.geometry.geometries[0].coordinates[1],
        ],
      },
    };
    return { item: poi, point, type: "poi" };
  }
}

function createClusterIcon(markerData: MarkerData[], cluster: MarkerCluster) {
  const clusterMarkers = cluster.getAllChildMarkers();

  let colors: { [key: string]: number } = {};
  const total = clusterMarkers.length;

  // Find color by marker and count colors
  clusterMarkers.forEach((child) => {
    const position = child.getLatLng();
    const marker = markerData.find(
      (marker) =>
        marker.point.geometry.coordinates[1] === position.lat &&
        marker.point.geometry.coordinates[0] === position.lng
    );
    const color =
      (marker?.type === "detour"
        ? detourMarkersIcons.get(marker.item.detourType.name)?.backgroundColor
        : marker?.item.type.styling.backgroundColor) ?? "#FFA20C";

    if (!colors[color]) {
      colors[color] = 0;
    }
    colors[color]++;
  });

  // Build icon
  let svgIcon = `
          <svg viewBox="0 0 42 42">
              <circle cx="18" cy="18" r="14" fill="white"></circle>`;

  // Draw paths in circle
  let currentTotalPercentage = 0;
  Object.keys(colors).forEach((color) => {
    const percentage = (100 * colors[color]) / total;
    const angle = (currentTotalPercentage / 100) * 360;

    svgIcon += `
              <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  transform="rotate(${angle}, 18, 18)"
                  stroke="${color}"
                  stroke-width="5"
                  stroke-dasharray="${percentage}, 100"
              />
          `;

    currentTotalPercentage += percentage;
  });

  svgIcon += `
          <text
              x="19"
              y="24"
              text-anchor="middle"
              style="font-size: 18px"
              class="">
              ${cluster.getChildCount()}
          </text>
      </svg>`;

  return divIcon({
    html: svgIcon,
    iconSize: new Point(42, 42),
    iconAnchor: [21, 21],
    tooltipAnchor: [84, 42],
  });
}
